import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, computed, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MusicDataService } from '../music-data.service';
import { FavouriteAlbumsService } from '../favourite-albums.service';
import { FavouriteArtistsService } from '../favourite-artists.service';
import { FavouriteHeartComponent } from '../shared/favourite-heart.component';
import { DeezerEmbedComponent } from '../shared/deezer-embed.component';
import { DeezerLinkComponent } from '../shared/deezer-link.component';
import { RelatedArtistsComponent } from '../shared/related-artists.component';
import { PlayerModeService } from '../shared/player-mode.service';
import { PlayerModeToggleComponent } from '../shared/player-mode-toggle.component';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardContent } from '@angular/material/card';
import { FlexModule } from '@ngbracket/ngx-layout/flex';

import { MatChipSet, MatChip } from '@angular/material/chips';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-album',
  templateUrl: './album.component.html',
  styleUrls: ['./album.component.css'],
  imports: [MatIcon, MatCard, MatCardContent, FlexModule, MatChipSet, MatChip, RouterLink, DeezerEmbedComponent, DeezerLinkComponent, PlayerModeToggleComponent, RelatedArtistsComponent, FavouriteHeartComponent]
})
export class AlbumComponent implements OnInit, OnDestroy {
  album = signal<any>({
    name: '', label: '', copyright: [], external_ids: '', external_url: '', release_date: '',
    total_tracks: 0, artists: [], popularity: 0, images: [], tracks: []
  });
  private idSub: Subscription;
  private albumSub: Subscription;
  private addFavSub: Subscription;

  currentPlayingId = signal<string | null>(null);
  private playing = signal(false);
  private audio: HTMLAudioElement | null = null;

  constructor(
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private data: MusicDataService,
    protected favourites: FavouriteAlbumsService,
    protected artistFavourites: FavouriteArtistsService,
    protected player: PlayerModeService,
  ) { }

  albumId = signal('');
  isFavouriteAlbum = computed(() => this.favourites.ids().has(this.albumId()));

  toggleFavouriteAlbum(): void {
    this.favourites.toggle(this.albumId(), this.album().name);
  }

  ngOnInit() {
    this.favourites.ensureIds();
    this.artistFavourites.ensureIds();
    this.idSub = this.route.params.subscribe(params => {
      const id = params.id;
      this.albumId.set(String(id));
      // Deezer mode: the whole album is ready to play in Deezer's player
      if (this.player.mode() === 'deezer') this.player.activeKey.set(`album:${id}`);
      this.albumSub = this.data.getAlbumById(id).subscribe((data) => {
        if (data) {
          this.album.set({
            name: data.name,
            label: data.label,
            copyright: data.copyrights,
            external_ids: data.external_ids,
            external_url: data.external_urls,
            release_date: data.release_date,
            images: data.images,
            tracks: data.tracks,  // Already an array from the service
            total_tracks: data.tracks?.length || 0,
            artists: data.artists,
            popularity: data.popularity
          });
        }
      });
    });
  }

  // In Deezer mode the play buttons open Deezer's embedded player under the song; previews use the local audio
  playTrack(track: any): void {
    if (this.player.mode() === 'deezer') {
      this.player.toggleActive(`track:${track.id}`);
      return;
    }
    if (!track.preview_url) {
      this.snackBar.open('No preview available for this track', 'OK', { duration: 2000 });
      return;
    }

    if (this.currentPlayingId() === track.id && this.audio) {
      // Toggle play/pause
      if (this.audio.paused) {
        this.audio.play();
        this.playing.set(true);
      } else {
        this.audio.pause();
        this.playing.set(false);
      }
    } else {
      // Stop current track and play new one
      if (this.audio) {
        this.audio.pause();
        this.audio = null;
      }
      this.audio = new Audio(track.preview_url);
      this.currentPlayingId.set(track.id);
      this.audio.play();
      this.playing.set(true);
      this.audio.onended = () => {
        this.currentPlayingId.set(null);
        this.playing.set(false);
        this.audio = null;
      };
    }
  }

  isPlaying(trackId: string): boolean {
    if (this.player.mode() === 'deezer') return this.player.activeKey() === `track:${trackId}`;
    return this.currentPlayingId() === trackId && this.playing();
  }

  addFavourites(i: number): void {
    const trackId = this.album().tracks[i].id;
    console.log('Adding to favourites, track ID:', trackId);
    this.addFavSub = this.data.addToFavourites(trackId).subscribe({
      next: () => {
        this.snackBar.open('Added to Favourites!', 'OK', { duration: 2000 });
      },
      error: (err) => {
        console.error('Error adding to favourites:', err);
        this.snackBar.open('Unable to Add to Favourites: ' + (err.message || 'Unknown error'), 'Error', { duration: 3000 });
      }
    });
  }
  ngOnDestroy() {
    this.idSub?.unsubscribe();
    this.albumSub?.unsubscribe();
    this.addFavSub?.unsubscribe();
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
  }
}
